import type { ReactNode } from 'react';
import type { BookingPublic, EventLocation, Slot } from '../api/types';
import { labelForStatus, type Labels } from '../i18n/labels';
import { useZeitwahl } from '../provider';
import { dateIn } from '../time/calendar';
import {
  formatDayLong,
  formatInstantDay,
  formatList,
  formatTimeRange,
  formatZoneLabel,
} from '../time/format';

export function locationText(location: EventLocation, labels: Labels): string | null {
  switch (location.kind) {
    case 'google_meet':
      return labels.locationGoogleMeet;
    case 'phone':
      return location.label ?? labels.locationPhone;
    case 'in_person':
      return location.address ?? location.label ?? labels.locationInPerson;
    case 'custom':
      return location.label ?? location.address ?? labels.locationCustom;
    default:
      return null;
  }
}

export function StatusBadge({ status }: { status: BookingPublic['status'] }): ReactNode {
  const { labels } = useZeitwahl();
  return <span className={`zw-status zw-status--${status}`}>{labelForStatus(labels, status)}</span>;
}

export interface BookingDetailsProps {
  booking: BookingPublic;
  /** Zone to display the time in; defaults to the zone the visitor booked in. */
  timeZone?: string;
  locale?: string;
  showStatus?: boolean;
}

/** Definition list with the booked time, hosts, location and reference — shared by confirmation and manage. */
export function BookingDetails({
  booking,
  timeZone,
  locale: localeProp,
  showStatus,
}: BookingDetailsProps): ReactNode {
  const { t, labels, locale: contextLocale } = useZeitwahl();
  const locale = localeProp ?? contextLocale;
  const tz = timeZone ?? booking.timeZone;
  const location = locationText(booking.location, labels);
  const hosts = booking.hosts.map((h) => h.name).filter(Boolean);

  return (
    <div className="zw-details">
      <div className="zw-details-head">
        <span className="zw-details-title">{booking.eventType.title}</span>
        {showStatus && <StatusBadge status={booking.status} />}
      </div>
      <p className="zw-details-when">
        <strong>{formatInstantDay(booking.start, locale, tz)}</strong>
        <span>{formatTimeRange(booking.start, booking.end, locale, tz)}</span>
        <span className="zw-muted">{formatZoneLabel(tz, locale)}</span>
      </p>
      {booking.rescheduledFrom && (
        <p className="zw-muted zw-details-prev">
          {t('managePreviously', {
            range: `${formatInstantDay(booking.rescheduledFrom.start, locale, tz)}, ${formatTimeRange(
              booking.rescheduledFrom.start,
              booking.rescheduledFrom.end,
              locale,
              tz
            )}`,
          })}
        </p>
      )}
      <ul className="zw-meta">
        {hosts.length > 0 && <li>{t('eventWith', { names: formatList(hosts, locale) })}</li>}
        {location && (
          <li>
            {booking.location.url ? (
              <a className="zw-link" href={booking.location.url} target="_blank" rel="noreferrer">
                {location}
              </a>
            ) : (
              location
            )}
          </li>
        )}
        {booking.location.instructions && (
          <li className="zw-muted">{booking.location.instructions}</li>
        )}
        <li className="zw-muted">{t('manageBookedFor', { email: booking.visitor.email })}</li>
        <li className="zw-muted">{t('confirmationReference', { ref: booking.publicRef })}</li>
      </ul>
    </div>
  );
}

export interface SelectedTimeProps {
  slot: Pick<Slot, 'startUtc' | 'end'>;
  timeZone: string;
  locale?: string;
}

/** Compact "Monday, March 30, 2026 · 9:00 AM – 9:30 AM · Europe/Zurich (GMT+2)" summary. */
export function SelectedTime({ slot, timeZone, locale: localeProp }: SelectedTimeProps): ReactNode {
  const { locale: contextLocale } = useZeitwahl();
  const locale = localeProp ?? contextLocale;
  return (
    <p className="zw-selected">
      <strong>{formatDayLong(dateIn(timeZone, slot.startUtc), locale, true)}</strong>
      <span>{formatTimeRange(slot.startUtc, slot.end, locale, timeZone)}</span>
      <span className="zw-muted">{formatZoneLabel(timeZone, locale)}</span>
    </p>
  );
}
